import { unitMessages } from './unit-messages';
import { WEIGHT_UNIT_GRAMS, WEIGHT_UNIT_KILOGRAMS, WEIGHT_UNIT_OUNCES, WEIGHT_UNIT_POUNDS } from '../types/weight';

import type { BaseWeightUnit, MajorWeightUnit, Weight, WeightUnit } from '../types/weight';
import type { IntlShape, MessageDescriptor } from 'react-intl';

const OUNCES_PER_POUND = 16;
const GRAMS_PER_KILOGRAM = 1000;
const DECIMAL_PRECISION = 100000;

const GRAMS_PER_UNIT: Record<WeightUnit, number> = {
  Grams: 1,
  Kilograms: 1000,
  Ounces: 28.3495,
  Pounds: 453.592,
};

/**
 * Convert a weight value between any two weight units.
 * Uses grams as an intermediate to avoid a 4×4 conversion matrix.
 */
export const convertWeight = (value: number, fromUnit: WeightUnit, toUnit: WeightUnit): number => {
  if (fromUnit === toUnit) return value;
  const grams = value * GRAMS_PER_UNIT[fromUnit];
  return grams / GRAMS_PER_UNIT[toUnit];
};

/** Combine pounds and ounces into total ounces */
export const combinePoundsAndOunces = (pounds: number, ounces: number): number => {
  return Math.max(pounds * OUNCES_PER_POUND + ounces, 0);
};

/** Combine kilograms and grams into total grams */
export const combineKilogramsAndGrams = (kilograms: number, grams: number): number => {
  return Math.max(kilograms * GRAMS_PER_KILOGRAM + grams, 0);
};

/**
 * Normalizes an API weight (any WeightUnit) to a Minor unit (Ounces or Grams).
 */
export const normalizeToMinorWeightUnit = (
  apiWeight: { value?: number; unit?: string } | null | undefined,
): { value: number; unit: BaseWeightUnit } => {
  if (!apiWeight?.unit) return { value: 0, unit: WEIGHT_UNIT_OUNCES };

  const value = apiWeight.value ?? 0;
  const { unit } = apiWeight;

  if (unit === WEIGHT_UNIT_OUNCES) return { value, unit: WEIGHT_UNIT_OUNCES };
  if (unit === WEIGHT_UNIT_GRAMS) return { value, unit: WEIGHT_UNIT_GRAMS };
  if (unit === WEIGHT_UNIT_POUNDS) return { value: value * OUNCES_PER_POUND, unit: WEIGHT_UNIT_OUNCES };
  if (unit === WEIGHT_UNIT_KILOGRAMS) return { value: value * GRAMS_PER_KILOGRAM, unit: WEIGHT_UNIT_GRAMS };

  console.warn(`normalizeApiWeight: unknown unit "${unit}", falling back to ${WEIGHT_UNIT_OUNCES}`);
  return { value, unit: WEIGHT_UNIT_OUNCES };
};

/**
 * Normalizes an API weight (any WeightUnit) to a major unit (Pounds or Kilograms).
 */
export const normalizeToMajorWeightUnit = (
  apiWeight: { value?: number; unit?: string } | null | undefined,
  params: { roundUp?: boolean } = {},
): { value: number; unit: MajorWeightUnit } => {
  const { roundUp = false } = params;
  const maybeRound = (value: number): number => {
    if (!roundUp) return value;
    const snapped = Math.round(value * DECIMAL_PRECISION) / DECIMAL_PRECISION;
    return Math.ceil(snapped);
  };

  if (!apiWeight?.unit) return { value: 0, unit: WEIGHT_UNIT_POUNDS };

  const value = apiWeight.value ?? 0;
  const { unit } = apiWeight;

  if (unit === WEIGHT_UNIT_POUNDS) return { value: maybeRound(value), unit: WEIGHT_UNIT_POUNDS };
  if (unit === WEIGHT_UNIT_KILOGRAMS) return { value: maybeRound(value), unit: WEIGHT_UNIT_KILOGRAMS };
  if (unit === WEIGHT_UNIT_OUNCES) return { value: maybeRound(value / OUNCES_PER_POUND), unit: WEIGHT_UNIT_POUNDS };
  if (unit === WEIGHT_UNIT_GRAMS) return { value: maybeRound(value / GRAMS_PER_KILOGRAM), unit: WEIGHT_UNIT_KILOGRAMS };

  console.warn(`normalizeToMajorWeightUnit: unknown unit "${unit}", falling back to ${WEIGHT_UNIT_POUNDS}`);
  return { value: maybeRound(value), unit: WEIGHT_UNIT_POUNDS };
};

/** Returns true if the weight unit is imperial (Ounces or Pounds). */
export const isImperialWeight = (unit: Weight['unit']): boolean => unit === WEIGHT_UNIT_OUNCES || unit === WEIGHT_UNIT_POUNDS;

/**
 * Format a weight value as a human-readable string in the target unit.
 * Converts from `fromUnit` to `toUnit`, then formats as "X lbs Y oz" or "X kg Y g".
 */
export const formatWeight = (value: number, fromUnit: WeightUnit, toUnit: BaseWeightUnit, intl: IntlShape): string => {
  const converted = convertWeight(value, fromUnit, toUnit);
  const { major, minor } = splitWeight({ value: converted, unit: toUnit });
  const displayMinor = Math.round(minor.value * 10) / 10;
  return `${major.value} ${intl.formatMessage(getWeightUnitMessage(major.unit))} ${displayMinor} ${intl.formatMessage(getWeightUnitMessage(minor.unit))}`;
};

/** Splits a weight of any unit into major (lb/kg) and minor (oz/g) Weight parts. */
export const splitWeight = (weight: Weight): { major: Weight; minor: Weight } => {
  const isImperial = isImperialWeight(weight.unit);
  const conversionRate = isImperial ? OUNCES_PER_POUND : GRAMS_PER_KILOGRAM;

  const normalized = normalizeToMinorWeightUnit(weight);
  // Pre-round to avoid Math.floor misclassifying near-integer floats (e.g. 47.9999… → 2 lb instead of 3 lb).
  const rounded = Math.round(normalized.value * DECIMAL_PRECISION) / DECIMAL_PRECISION;
  const majorValue = Math.floor(rounded / conversionRate);
  const minorValue = Math.round((rounded % conversionRate) * DECIMAL_PRECISION) / DECIMAL_PRECISION;

  return {
    major: { value: majorValue, unit: isImperial ? WEIGHT_UNIT_POUNDS : WEIGHT_UNIT_KILOGRAMS },
    minor: { value: minorValue, unit: isImperial ? WEIGHT_UNIT_OUNCES : WEIGHT_UNIT_GRAMS },
  };
};

export const FREIGHT_WEIGHT_UNIT_OF_MEASURE_ID: Record<number, string> = {
  1: 'Ounces',
  2: 'Grams',
  5: 'Pounds',
  6: 'Kilograms',
};
export const mapWeightUomId = (id: number | null | undefined): string =>
  id != null && FREIGHT_WEIGHT_UNIT_OF_MEASURE_ID[id] ? FREIGHT_WEIGHT_UNIT_OF_MEASURE_ID[id] : WEIGHT_UNIT_OUNCES;

export const getMajorUnitType = (weightUnit: WeightUnit): MajorWeightUnit => {
  switch (weightUnit) {
    case WEIGHT_UNIT_KILOGRAMS:
    case WEIGHT_UNIT_GRAMS:
      return WEIGHT_UNIT_KILOGRAMS;
    case WEIGHT_UNIT_POUNDS:
    case WEIGHT_UNIT_OUNCES:
    default:
      return WEIGHT_UNIT_POUNDS;
  }
};

export const getWeightUnitMessage = (weightUnit: WeightUnit): MessageDescriptor => {
  switch (weightUnit) {
    case WEIGHT_UNIT_KILOGRAMS:
      return unitMessages.kilograms;
    case WEIGHT_UNIT_GRAMS:
      return unitMessages.grams;
    case WEIGHT_UNIT_POUNDS:
      return unitMessages.pounds;
    case WEIGHT_UNIT_OUNCES:
    default:
      return unitMessages.ounces;
  }
};
